import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mediaUrl } from '../config/api';
import { collectionIcon } from '../shell/collections';
import { IconButton, Menu, Pill } from '../ui';
import { LocationIcon, LogoutIcon, MoreIcon, SettingsIcon, ShareIcon, TrashIcon } from '../ui/icons';

/** Up to four covers side by side, like spines on a shelf; the kind's icon when there are none. */
function CoverMosaic({ inventory }) {
  const [broken, setBroken] = useState([]);
  const ids = (inventory.cover_ids || []).filter((id) => !broken.includes(id)).slice(0, 4);
  const Icon = collectionIcon(inventory);
  if (ids.length === 0) {
    return (
      <div className="collection-card__media collection-card__media--icon" aria-hidden="true">
        <Icon size={36} strokeWidth={1.6} />
      </div>
    );
  }
  return (
    <div className="collection-card__media" aria-hidden="true">
      {ids.map((id) => (
        <img key={id} src={mediaUrl(id, 'thumb')} alt="" loading="lazy" decoding="async"
             onError={() => setBroken((list) => [...list, id])} />
      ))}
    </div>
  );
}

/**
 * The collections as cards (phase 2, slice 2): covers or the kind's icon,
 * name, item count and types, and for a shared one its owner and a read /
 * edit pill. The whole card opens the collection; "…" holds what the owner
 * may do (share, item types, delete) or "Leave" for one shared with them.
 */
function InventoryList({ inventories, onDelete, onLeave, onEditItemTypes, onShare, showAll, emptyState }) {
  const { t } = useTranslation();
  const mine = inventories.filter((i) => i.is_mine);
  const shared = inventories.filter((i) => !i.is_mine);

  // Titles under the page's h1 are h2; under "Shared with me" (an h2), h3.
  const renderCard = (inv, Title = 'h2') => {
    // The default type is marked when there are several.
    const many = (inv.item_types || []).length > 1;
    const types = (inv.item_types || []).map((type) => (many && type.is_default ? t('collections.defaultType', { name: type.name }) : type.name));
    const actions = inv.my_permission === 'owner'
      ? [
        { id: 'share', label: t('collections.share'), icon: ShareIcon, onSelect: () => onShare(inv) },
        { id: 'types', label: t('collections.editItemTypes'), icon: SettingsIcon, onSelect: () => onEditItemTypes(inv) },
        'separator',
        { id: 'delete', label: t('collections.deleteCollection'), icon: TrashIcon, danger: true, onSelect: () => onDelete(inv) },
      ]
      : [{ id: 'leave', label: t('collections.leave'), icon: LogoutIcon, danger: true, onSelect: () => onLeave(inv) }];
    const permission = inv.share_permission || (inv.is_mine ? null : inv.my_permission);
    return (
      <li key={inv.id} className="collection-card">
        <CoverMosaic inventory={inv} />
        <div className="collection-card__body">
          <Title className="collection-card__title">
            <Link to={`/c/${inv.id}`} className="collection-card__link">{inv.name}</Link>
          </Title>
          <p className="collection-card__meta">
            {[t('shell.itemCount', { count: Number(inv.item_count || 0) }), types.join(', ')].filter(Boolean).join(' · ')}
          </p>
          {inv.location_type_name && (
            <p className="collection-card__meta collection-card__places">
              <LocationIcon size={14} />
              <span>{inv.location_type_name} · {t('collections.locationCount', { count: inv.places ? inv.places.length : 0 })}</span>
            </p>
          )}
          {!inv.is_mine && (
            <p className="collection-card__owner">
              <span>{t('collections.by', { name: inv.owner_name })}</span>
              {(permission === 'read' || permission === 'edit') && (
                <Pill tone={permission === 'edit' ? 'accent' : 'amber'}>
                  <span aria-hidden="true">{t(permission === 'edit' ? 'shell.permEdit' : 'shell.permRead')}</span>
                  <span className="visually-hidden">{t(permission === 'edit' ? 'shell.permEditLong' : 'shell.permReadLong')}</span>
                </Pill>
              )}
            </p>
          )}
          {inv.description && <p className="collection-card__description">{inv.description}</p>}
        </div>
        <div className="collection-card__menu">
          <Menu
            label={t('collections.actionsFor', { name: inv.name })}
            align="end"
            items={actions}
            trigger={(props) => <IconButton {...props} icon={MoreIcon} variant="ghost" size="sm" label={t('collections.actionsFor', { name: inv.name })} />}
          />
        </div>
      </li>
    );
  };

  return (
    <div className="collections">
      {/* The page header (CollectionsHome) carries the title and "New collection". */}
      {mine.length === 0 ? emptyState : (
        <ul className="collection-grid" aria-label={t('shell.myCollections')}>
          {mine.map((inv) => renderCard(inv))}
        </ul>
      )}

      {shared.length > 0 && (
        <section aria-labelledby="collections-shared">
          <h2 id="collections-shared" className="collections-section-title">
            {showAll ? t('collections.otherAccounts') : t('collections.sharedWithMe')}
          </h2>
          <ul className="collection-grid">
            {shared.map((inv) => renderCard(inv, 'h3'))}
          </ul>
        </section>
      )}
    </div>
  );
}

export default InventoryList;
